import { Control, type IChannelGroup, type OrgaElementModel, SmartTimeWindow } from '..';
import { TreeEntityType } from './enum/TreeEntityType';
import type { IChannel } from './interfaces/channel.interface';
import type { IMeasurementConfigurationModel } from './interfaces/measurement-configuration-model.interface';

/**
 * Represents a dynamic flat node in a tree structure.
 * @template T The type of the entity associated with the node.
 */
export class DynamicFlatNode<T = OrgaElementModel | IChannel | IMeasurementConfigurationModel | IChannelGroup> {
  /**
   * Creates an array of new `DynamicFlatNode` instances from the given nodes.
   * @param nodes An array of `DynamicFlatNode` objects.
   * @returns An array of new `DynamicFlatNode` instances.
   */
  static makeNodes(nodes: DynamicFlatNode[]) {
    return nodes.map((n) => new DynamicFlatNode(n));
  }

  /**
   * Sorts an array of `DynamicFlatNode` objects by their labels in ascending order (case-insensitive).
   * @param dfnList The list of `DynamicFlatNode` objects to sort.
   * @returns A sorted array of `DynamicFlatNode` objects.
   */
  static readonly sortByLabel = (dfnList: DynamicFlatNode[]): DynamicFlatNode[] => {
    if (!dfnList?.length) {
      return [];
    }
    return dfnList.filter((a) => !!a?.label).sort((f, s) => (f?.label.toLowerCase() > s?.label.toLowerCase() ? 1 : -1));
  };

  /**
   * Sorts an array of `DynamicFlatNode` objects by their labels in descending order (case-insensitive).
   * @param dfnList The list of `DynamicFlatNode` objects to sort.
   * @returns A sorted array of `DynamicFlatNode` objects in reverse order.
   */
  static readonly sortByLabelReverse = (dfnList: DynamicFlatNode[]): DynamicFlatNode[] => {
    if (!dfnList?.length) {
      return [];
    }
    return DynamicFlatNode.sortByLabel(dfnList).reverse();
  };

  /**
   * Returns a dummy product node.
   * @returns A dummy `DynamicFlatNode` instance.
   */
  static get dummyProduct() {
    return new DynamicFlatNode({
      label: 'no',
      id: 'no',
      level: 0,
      expandable: false,
      isLoading: false,
      type: TreeEntityType.USE_CASE_ROOT,
      entity: {} as OrgaElementModel,
    });
  }

  /**
   * The label of the node.
   */
  public label: string;

  /**
   * The unique identifier of the node.
   */
  public id: string;

  /**
   * The level of the node in the tree structure.
   */
  public level = 1;

  /**
   * Indicates whether the node is expandable.
   */
  public expandable = false;

  /**
   * Indicates whether the node is currently loading.
   */
  public isLoading = false;

  /**
   * Indicates whether the node is hidden.
   */
  public hidden = false;

  /**
   * The type of the node.
   */
  public type: string;

  /**
   * The entity associated with the node.
   */
  public entity: T;

  /**
   * Indicates whether the node has an error.
   */
  public hasError = false;

  /**
   * Creates a new instance of `DynamicFlatNode`.
   * @param obj An object containing the properties to initialize the node.
   */
  constructor(obj: {
    label: string;
    level: number;
    expandable: boolean;
    isLoading: boolean;
    type: string;
    id: string;
    entity: T;
  }) {
    Object.assign(this, obj);
    this.label = obj.label;
    this.level = obj.level;
    this.expandable = obj.expandable;
    this.isLoading = obj.isLoading;
    this.type = obj.type;
    this.id = obj.id;
    this.entity = obj.entity;
  }

  /**
   * Sets the data source for the node.
   * @param parentIdentifier The identifier of the parent node.
   * @returns The current `DynamicFlatNode` instance.
   */
  setDatasource(parentIdentifier: string) {
    this.entity = { parentIdentifier } as T;
    return this;
  }

  /**
   * Converts the node to a `Control` object.
   * @returns A `Control` instance representing the node.
   */
  toControl(): Control {
    const nodetime = this.#extractNodeTimeWindowResolution();
    return new Control({
      id: this.id,
      label: this.label,
      entity: this.entity,
      windowSize: nodetime.windowSize,
      windowUnit: nodetime.windowUnit,
      unit: (this.entity as IChannel)?.unit,
    });
  }

  /**
   * Finds the parent node of the current node in the given list of nodes.
   * @param nodes The list of `DynamicFlatNode` objects to search.
   * @returns The parent `DynamicFlatNode` instance, or undefined if not found.
   */
  findParentIn(nodes: DynamicFlatNode[]) {
    return nodes.find(({ id }) => id === (this.entity as OrgaElementModel)?.parentIdentifier);
  }

  /**
   * Extracts the time window resolution for the node.
   * @returns A `SmartTimeWindow` resolution object.
   */
  #extractNodeTimeWindowResolution() {
    const { entity } = this as DynamicFlatNode<IChannel>;
    const latestTimestamp = new Date(entity.latestTimestamp);
    const firstTimestamp = new Date(entity.firstTimestamp);
    return new SmartTimeWindow(latestTimestamp.getTime() - firstTimestamp.getTime()).getResolution();
  }

  /**
   * Checks if the node is of the specified type.
   * @param CHANNEL The type to check against.
   * @returns `true` if the node is of the specified type, otherwise `false`.
   */
  isTypeOf(CHANNEL: TreeEntityType) {
    return this.type === CHANNEL;
  }

  /**
   * Checks if the current node is the parent of the given node.
   * @param instanceNode The node to check.
   * @returns `true` if the current node is the parent, otherwise `false`.
   */
  isParentOf(instanceNode: DynamicFlatNode) {
    return (this.entity as any).parentIdentifier === instanceNode.id;
  }
}
